import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import { useTypedSelector } from '@cube3/state/src/redux/components/Hooks/useTypedSelector';
import { useUploads } from '@cube3/state/src/redux/components/Hooks/useUploads';
import {
  actionCreators,
  selectors,
  statuses
} from '@cube3/state/src/redux/ducks/request-status';
import { actionCreators as nodeActionCreators } from '@cube3/state/src/redux/ducks/resource-nodes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAssetsPoll } from '../../ContentPane/ContentTree/hooks/useAssetsPoll';

export const useUploadQueueController = () => {
  const dispatch = useDispatch();
  const [workspaceId] = useCurrentWorkspace();

  const { allUploads } = useUploads();

  // track broken uploads
  const failed = useMemo(() => {
    return allUploads.filter((u) => u.failed || (u.active && !u.tusUpload && u['status'] !== 'succeeded'));
  }, [allUploads]);

  // track finished uploads
  const inactive = useMemo(() => {
    return allUploads.filter((u) => !u.failed && u.progress === 100);
  }, [allUploads]);

  // clear all finished uploads (not the uploaded assets)
  const handleClearFinished = useCallback(() => {
    inactive.forEach((u) => {
      dispatch(nodeActionCreators.deleteResource('file-upload', u.id));
      dispatch(nodeActionCreators.disposeResource('file-upload', u.id));
    });
  }, [inactive, dispatch]);

  // clear all failed uploads (not the uploaded assets)
  const handleClearFailed = useCallback(() => {
    failed.forEach((u) => {
      dispatch(nodeActionCreators.deleteResource('file-upload', u.id));
      dispatch(nodeActionCreators.disposeResource('file-upload', u.id));

      if (u.asset_id) {
        dispatch(nodeActionCreators.deleteResource('asset', u.asset_id));
        dispatch(nodeActionCreators.disposeResource('file-upload', u.asset_id));

      }
    });
  }, [failed, dispatch]);

  // sort by date latest first
  const sortedUploads = useMemo(() => {
    return allUploads.sort((a, b) => {
      return (
        new Date(b.initiated_at || b.created_at || 0).getTime() -
        new Date(a.initiated_at || a.created_at || 0).getTime()
      );
    });
  }, [allUploads]);

  // for virtual scrolling
  const [pageSize, setPageSize] = useState(16);
  const [focusItem, setFocusItem] = useState(0);

  // select a range of items from the larger set
  const items = useMemo(() => {
    const low = Math.max(focusItem - pageSize, 0);
    const high = Math.min(focusItem + pageSize, sortedUploads.length);
    const set = sortedUploads.slice(low, high);
    const items = set.map((i, idx) => ({ ...i, pos: low + idx }));
    return items;
  }, [sortedUploads, focusItem, pageSize]);

  const { polledAssets } = useAssetsPoll({
    uploads: allUploads,
    pollDuration: 3 * 1000,
    idlePollDuration: 30 * 1000
  });

  // find asset ids linked to uploads, but not returned from workspace assets endpoint
  // this means they have probably been deleted
  const maybeDeleted = allUploads?.filter((u) => {
    if (!polledAssets.resources || !u.asset_id) {
      return;
    }

    if (polledAssets.resources.find((a) => a.id === u.asset_id)) {
      return;
    }

    return true;
  });

  // find assets that are probably delted, but are still available via the cache layer
  // e.g. assets in a folder that has been deleted
  // this needs to check success status, to prevent us from invalidating assets over and over
  // we also do some quick&dirty memoization so the useSelector doesn't start spamming
  const lastStatuses = useRef(null);
  const invalidate = useTypedSelector((state) => {
    let dirty =
      !lastStatuses.current ||
      maybeDeleted.length !== lastStatuses.current.length;
    const newStatuses = maybeDeleted.map((u, idx) => {
      const { asset_id } = u;
      const status = selectors.getStatus(
        state.requestStatus,
        nodeActionCreators.retrieveResource('asset', asset_id)
        // NOTE: above action isn't dispatched, but serialized, and used as identifier
      );

      const maybe = status === statuses.SUCCESS ? u : undefined;

      if (dirty || lastStatuses.current[idx] !== maybe) {
        dirty = true;
      }
      return maybe;
    });
    if (dirty) {
      lastStatuses.current = newStatuses;
    }
    return lastStatuses.current;
  });

  // invalidate cache for assets that arent listed, but still in the cache layer
  useEffect(() => {
    invalidate.forEach((u) => {
      if (u) {
        dispatch(
          actionCreators.invalidateResource({ type: 'asset', id: u.asset_id })
        );
      }
    });
  }, [invalidate, dispatch]);

  // make sure we load the list of assets before rendering,
  // otherwise individual tiles will load assets themselves
  const [fresh, setFresh] = useState(
    !allUploads.filter((u) => !!u.asset_id).length
  ); // if there's no uploads in queue we can skip waiting
  const allStatus = polledAssets.status;
  useEffect(() => {
    if (allStatus === statuses.SUCCESS) {
      setFresh(true);
    }
  }, [allStatus, setFresh]);

  // invalidate list when panel closes,
  // so we can wait for "fresh" the next time it opens
  useEffect(() => {
    return () => {
      dispatch(
        actionCreators.invalidateRelationship({
          type: 'workspace',
          id: workspaceId,
          relationship: 'assets'
        })
      );
    };
  }, [workspaceId, dispatch]);

  return {
    fresh,
    sortedUploads,
    inactive,
    failed,
    handleClearFinished,
    handleClearFailed,
    items,
    focusItem,
    setFocusItem,
    pageSize,
    setPageSize
  };
};
